import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { BASE_URL } from "@/config/api"; // Adjust the path as needed
import { getAccessToken } from "@/utils/token_functions"; // Adjust the path as needed
import { secureAxios } from "@/middleware/encryptionMiddleware";


// Async thunk for creating a town hall
export const createTownHall = createAsyncThunk(
  "townhall/create",
  async (townHallData, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      const response = await secureAxios.post(
        `${BASE_URL}/v1/townhall`,
        townHallData, // Send the town hall data as a single object
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

export const fetchTownHalls = createAsyncThunk(
  "townhall/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      const response = await secureAxios.get(`${BASE_URL}/v1/townhall`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchTownHallByAny = createAsyncThunk(
  "townhall/fetchTownHallByAny",
  async ({ key, value }, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      const response = await secureAxios.get(
        `${BASE_URL}/v1/townhall/by-any/${key}/${value}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return {
        data: response.data.data,
        total: response.data.data.length, // Include the total count
        isSingleTownHall: key === "_id", // Flag to indicate if we're fetching by ID
      };
    } catch (error) {
      // If it's a 404 error (not found), return an empty array instead of rejecting
      if (error.response?.status === 404) {
        return { data: [], total: 0, isSingleTownHall: false };
      }
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

export const updateTownHall = createAsyncThunk(
  "townhall/updateTownHall",
  async (townhallData, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      const response = await secureAxios.patch(
        `${BASE_URL}/v1/townhall`, // No ID in the URL
        townhallData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

export const deleteTownHall = createAsyncThunk(
  "townhall/deleteTownHall",
  async (townhallId, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      const response = await secureAxios.delete(`${BASE_URL}/v1/townhall`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: [townhallId], // Send directly as array: ["townhallId"]
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

const initialState = {
  townHalls: [],
  currentTownHall: null,
  fetchedTownHalls: [],
  total: 0, // Add total count
  isLoading: false,
  error: null,
  success: false,
};

// Town hall slice
const townhallSlice = createSlice({
  name: "townhall",
  initialState,
  reducers: {
    resetTownHallState: () => initialState,

    loadCachedData: (state, action) => {
      return {
        ...state,
        townHalls: action.payload.townHalls || state.townHalls,
        currentTownHall:
          action.payload.currentTownHall || state.currentTownHall,
        fetchedTownHalls:
          action.payload.fetchedTownHalls || state.fetchedTownHalls,
        total: action.payload.total || state.total,
        isLoading: false,
        error: null,
        // Flag to indicate data is from offline cache
        isOfflineData: true,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createTownHall.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(createTownHall.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = true;
        state.townHalls.push(action.payload); // Add the new town hall to the list
      })
      .addCase(createTownHall.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchTownHalls.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTownHalls.fulfilled, (state, action) => {
        state.isLoading = false;
        state.townHalls = action.payload.data;
      })
      .addCase(fetchTownHalls.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchTownHallByAny.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTownHallByAny.fulfilled, (state, action) => {
        state.isLoading = false;
        state.fetchedTownHalls = action.payload.data;
        state.total = action.payload.total;

        if (action.payload.isSingleTownHall && action.payload.data.length > 0) {
          // If we're fetching by ID, store as currentTownHall
          state.currentTownHall = action.payload.data[0];
        } else {
          state.currentTownHall =
            action.payload.data.length > 0 ? action.payload.data[0] : null;
        }
        state.error = null;
      })
      .addCase(fetchTownHallByAny.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(updateTownHall.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateTownHall.fulfilled, (state) => {
        state.isLoading = false;
        state.success = true;
      })
      .addCase(updateTownHall.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(deleteTownHall.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteTownHall.fulfilled, (state) => {
        state.isLoading = false;
        state.success = true;
      })
      .addCase(deleteTownHall.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

// Export actions
export const { resetTownHallState, loadCachedData } = townhallSlice.actions;

// Export selectors
export const selectTownHalls = (state) => state.townhall.townHalls;
export const selectTownHallLoading = (state) => state.townhall.isLoading;
export const selectTownHallError = (state) => state.townhall.error;
export const selectTownHallSuccess = (state) => state.townhall.success;
export const selectCurrentTownHall = (state) => state.townhall.currentTownHall;
export const selectIsOfflineData = (state) =>
  state.townhall.isOfflineData || false;
export const selectFetchedTownHalls = (state) =>
  state.townhall.fetchedTownHalls || [];
export const selectTownHallTotal = (state) => state.townhall.total || 0;

// Export reducer
export default townhallSlice.reducer;
